// Cookie-based user recognition system for eSIM webapp
// Manages long-lived UUID cookies for user recognition before authentication
import { captureSentryMessage } from './sentry';

const COOKIE_NAME = 'jetogo_user_uuid';
const COOKIE_TTL_YEARS = 10; // 10-year cookie lifetime
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const cookieManager = {
  /**
   * Set UUID cookie with 10-year TTL
   * @param {string} uuid - User UUID from pending_payments or users table
   */
  setUserUuid(uuid) {
    if (!uuid) {
      console.error('Cannot set cookie: UUID is required');
      return false;
    }

    if (!UUID_REGEX.test(uuid)) {
      console.error(`Cannot set cookie: invalid UUID format: ${uuid}`);
      captureSentryMessage('cookieManager.setUserUuid rejected invalid UUID', 'warning', {
        value: String(uuid).slice(0, 64),
      });
      return false;
    }

    try {
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + COOKIE_TTL_YEARS);

      document.cookie = `${COOKIE_NAME}=${uuid}; expires=${expirationDate.toUTCString()}; path=/; secure; SameSite=Strict`;

      console.log(`UUID cookie set: ${uuid} (expires: ${expirationDate.toLocaleDateString()})`);
      return true;
    } catch (error) {
      console.error('Failed to set UUID cookie:', error);
      return false;
    }
  },

  /**
   * Get UUID from cookie. If the stored value isn't a valid UUID, clear the
   * cookie, report to Sentry, and return null so callers treat the visitor as new.
   * @returns {string|null} - UUID if found and valid, null otherwise
   */
  getUserUuid() {
    try {
      const cookies = document.cookie.split(';');

      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === COOKIE_NAME) {
          if (!value) return null;

          if (!UUID_REGEX.test(value)) {
            captureSentryMessage('cookieManager cleared invalid jetogo_user_uuid cookie', 'warning', {
              value: value.slice(0, 64),
            });
            this.clearUserUuid();
            return null;
          }

          return value;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to get UUID cookie:', error);
      return null;
    }
  },

  /**
   * Check if user UUID cookie exists
   * @returns {boolean}
   */
  hasUserUuid() {
    return this.getUserUuid() !== null;
  },

  /**
   * Clear UUID cookie (logout/reset)
   */
  clearUserUuid() {
    try {
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure; SameSite=Strict`;
      console.log('UUID cookie cleared');
      return true;
    } catch (error) {
      console.error('Failed to clear UUID cookie:', error);
      return false;
    }
  },

  /**
   * Update existing cookie with new UUID (for pending_payments → users transfer)
   * @param {string} newUuid - New UUID to update cookie with
   */
  updateUserUuid(newUuid) {
    if (!newUuid) {
      console.error('Cannot update cookie: New UUID is required');
      return false;
    }
    
    return this.setUserUuid(newUuid);
  },

  /**
   * Get cookie metadata for debugging
   * @returns {object} - Cookie information and status
   */
  getCookieInfo() {
    const uuid = this.getUserUuid();
    const exists = uuid !== null;
    
    return {
      exists,
      uuid,
      cookieName: COOKIE_NAME,
      ttlYears: COOKIE_TTL_YEARS,
      rawCookieString: document.cookie
    };
  }
};

export default cookieManager;